import { format, isSameYear } from 'date-fns'
import { Link as RouterLink } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { TableCell, TableRow } from '@/components/ui/table'
import type { AppointmentRecord } from '@/features/appointments/types'
import {
  appointmentDate,
  isAppointmentActive,
  shortProviderName,
  splitAppointmentType,
} from '@/features/patients/utils/clientHistory'
import { cn } from '@/lib/utils'
import { SHEET_TABLE_CELL } from './sheetTableStyles'

/** "Sep 9, 10:30 AM" when the time is known; the year only when it differs. */
function whenLabel(appointment: AppointmentRecord, now: Date): string {
  const date = appointmentDate(appointment)
  if (!date) return 'No date'
  const day = format(date, isSameYear(date, now) ? 'MMM d' : 'MMM d, yyyy')
  return appointment.start_time ? `${day}, ${format(date, 'h:mm a')}` : day
}

export function ClientAppointmentRow({
  appointment,
  now,
}: {
  appointment: AppointmentRecord
  now: Date
}) {
  const { code, description } = splitAppointmentType(
    appointment.appointment_type
  )
  const reason = appointment.appointment_reason?.trim() || description
  const active = isAppointmentActive(appointment)
  const status = appointment.status ?? appointment.source_status

  return (
    // The date cell's link stretches over the row, so the whole row opens the
    // appointment's own sheet while the cells keep plain table semantics.
    <TableRow
      className={cn(
        'relative',
        !active && 'text-muted-foreground line-through'
      )}
      title={active ? undefined : (status ?? undefined)}>
      <TableCell className={cn(SHEET_TABLE_CELL, 'truncate tabular-nums')}>
        <RouterLink
          to={`/data/appointments?appointments.id=${appointment.id}&details=1`}
          className="focus-visible:ring-primary-solid rounded-sm after:absolute after:inset-0 focus-visible:ring-2 focus-visible:outline-none">
          {whenLabel(appointment, now)}
        </RouterLink>
      </TableCell>
      <TableCell
        className={cn(SHEET_TABLE_CELL, 'truncate')}
        title={code ?? undefined}>
        {code ?? '—'}
      </TableCell>
      <TableCell
        className={cn(SHEET_TABLE_CELL, 'truncate')}
        title={reason ?? undefined}>
        {reason ?? '—'}
      </TableCell>
      <TableCell
        className={cn(SHEET_TABLE_CELL, 'truncate')}
        title={appointment.provider_name ?? undefined}>
        {shortProviderName(appointment.provider_name) ?? '—'}
      </TableCell>
      <TableCell className={SHEET_TABLE_CELL}>
        {appointment.source_name ? (
          <Badge variant="outline">{appointment.source_name}</Badge>
        ) : null}
      </TableCell>
    </TableRow>
  )
}
